import {
  EXAMPLE_NAMES,
  MAIL_TEMPLATE_KINDS,
  PLACEHOLDERS,
  PLACEHOLDERS_BY_KIND
} from '../constants/mail-template-placeholders'
import {
  useAivyContext,
  useLanguageContext,
  usePartnerContext
} from '../context'
import {
  computeExpiresAt,
  formatAssessmentDeadline,
  getExpiresAfterDays
} from '../helpers/assessment-deadline'
import {
  MAIL_TEMPLATE_FALLBACK_LANGUAGE,
  resolveMailTemplateLanguage
} from '../helpers/mail-template-language'

const I18N_PLACEHOLDER = 'mail_template.edit_mail_template.placeholder.'

const calculateAssessmentDuration = ({ career, partner, language }) => {
  const { app_settings } = career || partner

  const minutes =
    JSON.parse(app_settings?.challenges || '[]').length * 2.5 || 25

  const durations = {
    de: `${minutes} Minuten`,
    en: `${minutes} minutes`,
    nl: `${minutes} minuten`,
    fr: `${minutes} minutes`
  }

  return durations[language] || durations[MAIL_TEMPLATE_FALLBACK_LANGUAGE]
}

// Beispielwerte für Vorschau und Menü. Die Test-Mail setzt inviteTalent mit
// denselben Werten zusammen (sendTestMail). Die Frist rechnet wie bei einer
// Einladung heute, mit der Bearbeitungszeit der Stelle und derselben
// Formatierung. Ein übergebenes expiresAt gilt stattdessen, etwa bei der
// Freischaltung eines bestehenden Space.
const getExampleValues = ({ career, partner, language, now, expiresAt }) => {
  const names = EXAMPLE_NAMES[language] || EXAMPLE_NAMES.en

  return {
    COMPANY_NAME: `${partner.display_name || partner.name}`,
    CAREER_TITLE: career?.title,
    ASSESSMENT_DURATION: calculateAssessmentDuration({
      career,
      partner,
      language
    }),
    ASSESSMENT_DEADLINE: formatAssessmentDeadline(
      expiresAt ||
        computeExpiresAt(
          getExpiresAfterDays(partner.app_settings, career?.app_settings),
          now
        ),
      language
    ),
    FIRSTNAME: names.firstname,
    LASTNAME: names.lastname
  }
}

/**
 * Platzhalter, die eine Vorlage eines Typs enthalten darf, mit Beispielwert.
 * Ohne Stelle entfällt der Stellentitel. Versteckte Platzhalter (alter
 * Start-Link) haben keinen Wert, die Vorschau ersetzt sie selbst.
 *
 * @param {object} params
 * @param {string} params.kind invite, reminder, reject oder subject
 * @param {object} [params.career]
 * @param {object} params.partner
 * @param {string} params.language aufgelöste Sprache der Mail
 * @param {Date} [params.now]
 * @param {string} [params.expiresAt] festes Fristende statt heute plus Bearbeitungszeit
 * @returns {{ macro: string, label: string, description: string, value?: string, hidden?: boolean }[]}
 */
export const buildMailTemplatePlaceholders = ({
  kind,
  career,
  partner,
  language,
  now = new Date(),
  expiresAt
}) => {
  const values = getExampleValues({
    career,
    partner,
    language,
    now,
    expiresAt
  })

  return PLACEHOLDERS_BY_KIND[kind]
    .filter((name) => career || !PLACEHOLDERS[name].requiresCareer)
    .map((name) => {
      const { token, key, hidden } = PLACEHOLDERS[name]

      return {
        macro: token,
        label: `${I18N_PLACEHOLDER}${key}.label`,
        description: `${I18N_PLACEHOLDER}${key}.description`,
        ...(hidden ? { hidden: true } : { value: values[name] })
      }
    })
}

export const useMailTemplateConstants = ({
  career,
  isCrossmode,
  expiresAt
}) => {
  const { system } = useAivyContext()
  const { partner } = usePartnerContext()
  const { language: contextLanguage } = useLanguageContext()

  // Eine aufgelöste Sprache für die ganze Mail. Kennt System/MAIL_TEMPLATES
  // die Sprache der Stelle (z. B. nl, fr) noch nicht, gilt überall en.
  const language = resolveMailTemplateLanguage({
    system,
    language: career?.language || contextLanguage
  })

  const buildFor = (kind) =>
    buildMailTemplatePlaceholders({
      kind,
      career,
      partner,
      language,
      expiresAt
    })

  const getMailTemplateMacros = () => buildFor(MAIL_TEMPLATE_KINDS.INVITE)

  const getMailTemplateMacrosReminder = () =>
    buildFor(MAIL_TEMPLATE_KINDS.REMINDER)

  const getMailTemplateMacrosReject = () => buildFor(MAIL_TEMPLATE_KINDS.REJECT)

  // Firma und Stelle werden schon beim Laden in den Betreff eingesetzt
  const getMailTemplateSubjectMacros = () =>
    buildFor(MAIL_TEMPLATE_KINDS.SUBJECT)

  const getDefaultMailTemplate = () => {
    const {
      defaultCareerMailTemplate,
      defaultPartnerMailTemplate,
      defaultCrossmodeMailTemplate
    } = system

    const result = {}

    if (career) {
      result.subject = defaultCareerMailTemplate.subject[language]
      result.data = defaultCareerMailTemplate.data[language]
    } else if (isCrossmode) {
      result.subject = defaultCrossmodeMailTemplate.subject[language]
      result.data = defaultCrossmodeMailTemplate.data[language]
    } else {
      result.subject = defaultPartnerMailTemplate.subject[language]
      result.data = defaultPartnerMailTemplate.data[language]
    }

    return { ...result, data: JSON.stringify(result.data) }
  }

  const getDefaultReminderMailTemplate = () => {
    const {
      defaultCareerMailTemplateReminder,
      defaultPartnerMailTemplateReminder
    } = system

    const result = {}

    if (career) {
      result.subject = defaultCareerMailTemplateReminder.subject[language]
      result.data = defaultCareerMailTemplateReminder.data[language]
    } else {
      result.subject = defaultPartnerMailTemplateReminder.subject[language]
      result.data = defaultPartnerMailTemplateReminder.data[language]
    }

    return { ...result, data: JSON.stringify(result.data) }
  }

  const getDefaultRejectMailTemplate = () => {
    const {
      defaultCareerMailTemplateReject,
      defaultPartnerMailTemplateReject
    } = system

    const result = {}

    if (career) {
      result.subject = defaultCareerMailTemplateReject.subject[language]
      result.data = defaultCareerMailTemplateReject.data[language]
    } else {
      result.subject = defaultPartnerMailTemplateReject.subject[language]
      result.data = defaultPartnerMailTemplateReject.data[language]
    }

    return { ...result, data: JSON.stringify(result.data) }
  }

  return {
    mailTemplateLanguage: language,
    getDefaultMailTemplate,
    getDefaultReminderMailTemplate,
    getDefaultRejectMailTemplate,
    getMailTemplateMacros,
    getMailTemplateMacrosReminder,
    getMailTemplateMacrosReject,
    getMailTemplateSubjectMacros
  }
}
